import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { Text } from '@abyss/web/ui/Text';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { useCategoryLLMFlag } from '../../hooks/useCategoryLLMFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { isFacility } from '../../utils/smartChoice.utils';
import { getVisibleFieldsConfig } from '../../utils/smartChoice.utils.audit';
import {
  getLoggedInMember,
  getMarketType,
  getProductCodeForCoverageType,
  useCustomPolicy,
} from '../../utils/user.utils';
import { ReverseCoverageTypesCodes } from '../Constants';
import { phoneOnly } from '../ConstantsStyles';
import { ContentWrapper } from '../ContentWrapper';
import {
  defaultStringToEmpty,
  defaultToZero,
  distanceParser,
  formattedValue,
  getAcoBenefitPlan,
  getFlagValue,
  getLLMStatus,
  getLanguageFormatted,
  useSuppressPremiumSmartchoice,
} from '../Widget/SmartChoiceWidget/utils';
import { StyledDrawer } from './SmartChoice.styled';

export const properties: any = {
  drawerSize: 492,
  drawerposition: 'right',
};

export interface Props {
  providerType?: string;
  isOpen: boolean;
  handleCloseDrawer: () => void;
  recommendationDetails: any;
  isTieredPlan?: boolean;
  preferencesData: any;
  stateCode: string;
  selectedChip: string;
}

export const SmartChoiceDetails = ({
  isOpen,
  handleCloseDrawer,
  recommendationDetails,
  isTieredPlan,
  preferencesData,
  stateCode,
  selectedChip,
  providerType = '',
}: Props) => {
  const { t } = useTranslation();
  const mobileScreen = useMediaQuery(phoneOnly);
  const enableLLM: boolean = useCategoryLLMFlag(providerType, selectedChip);
  const isWidget = useStore(StoreKeys?.IS_WIDGET);

  // NEW: Hybrid approach - arrays of visible fields (eliminates false condition checks)
  const visibleFields = getVisibleFieldsConfig(selectedChip, providerType);
  const isFacilityContext = isFacility(selectedChip, providerType);

  const {
    quality,
    benefits,
    convenience,
    personalFit,
    verification,
    randomization,
    billingCode: billingCodeRaw,
    bandedCost: bandedCostRaw,
  } = recommendationDetails;

  const qualityScore = quality?.scoreDetails?.value;
  const benefitsScore = benefits?.scoreDetails?.value;
  const convenienceScore = convenience?.scoreDetails?.value;
  const personalFitScore = personalFit?.scoreDetails?.value;
  const verificationScore = verification?.scoreDetails?.value;
  const randomizationScore = randomization?.scoreDetails?.value;
  const qualityPercentage = quality?.scoreDetails?.percentage;
  const benefitsPercentage = benefits?.scoreDetails?.percentage;
  const conveniencePercentage = convenience?.scoreDetails?.percentage;
  const personalFitPercentage = personalFit?.scoreDetails?.percentage;
  const verificationPercentage = verification?.scoreDetails?.percentage;
  const randomizationPercentage = randomization?.scoreDetails?.percentage;
  const billingCode = defaultStringToEmpty(billingCodeRaw);
  const bandedCost = defaultStringToEmpty(bandedCostRaw);
  const llmScoreText = isWidget
    ? billingCode
      ? getLLMStatus(quality?.llmStatus, t)
      : t('N/A')
    : enableLLM
    ? getLLMStatus(quality?.llmStatus, t)
    : t('N/A');
  // OPTIMIZED: No eager destructuring - access fields directly only when needed
  // This prevents creating unused variables for fields that won't render
  const randomizationKey = preferencesData?.memberRandomizedKey;
  const supressPremium = useSuppressPremiumSmartchoice(stateCode);
  // Keep these for backward compatibility (used in verification section)
  const { officeLocationScore, phoneConfidenceRecommendationScore } =
    verification;
  // Keep these as they're computed/transformed values
  const distance = convenience?.distance != null ? convenience?.distance : '';
  const formattedDist = distanceParser(distance);
  const affiliation = getFlagValue(benefits?.acoParticipationStatus);
  const preferredProviderStatus = getFlagValue(
    benefits?.preferredProviderStatus
  );
  const selectedMember = getLoggedInMember();
  const allConfigs = useLagoon('config')();
  const acoBenefitPlanCollection = allConfigs?.find(
    (row) => row.key === 'ACO_BENEFIT_COLLECTION'
  )?.value;

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { lob } = chipStore;

  const { isCustomPreferredPolicy } = useCustomPolicy(
    selectedMember,
    useLagoon('custom-preferred-policies'),
    lob
  );

  const isAcoBenefitPlan = getAcoBenefitPlan(
    defaultStringToEmpty(
      getProductCodeForCoverageType(
        selectedMember,
        ReverseCoverageTypesCodes.MEDICAL
      )
    ),
    defaultStringToEmpty(getMarketType(selectedMember)),
    defaultStringToEmpty(selectedMember?.membershipCategory),
    defaultStringToEmpty(selectedMember?.population),
    acoBenefitPlanCollection
  );

  const renderOverallRecommendationScore = (
    t: any,
    qualityScore: any,
    benefitsScore: any,
    convenienceScore: any,
    personalFitScore: any,
    verificationScore: any,
    randomizationScore: any
  ) => {
    const qualityScoreValue = defaultToZero(qualityScore);
    const benefitsScoreValue = defaultToZero(benefitsScore);
    const convenienceScoreValue = defaultToZero(convenienceScore);
    const personalFitScoreValue = defaultToZero(personalFitScore);
    const verificationScoreValue = defaultToZero(verificationScore);
    const randomizationScoreValue = defaultToZero(randomizationScore);
    const totalScore =
      parseFloat(qualityScoreValue) +
      parseFloat(benefitsScoreValue) +
      parseFloat(convenienceScoreValue) +
      parseFloat(personalFitScoreValue) +
      parseFloat(verificationScoreValue) +
      parseFloat(randomizationScoreValue);

    return (
      <Flex alignItems="flex-end" justify="space-between">
        <Text css={{ fontSize: '18px', fontWeight: '$bold' }}>
          {t('SMART_CHOICE.AUDIT_SCREEN.Overall Score')}:
        </Text>
        {totalScore !== null ? (
          <Text css={{ fontSize: '15px', fontWeight: '$bold' }}>
            {totalScore}
          </Text>
        ) : (
          <Text css={{ fontSize: '15px', fontWeight: '$bold' }}>
            {t('N/A')}
          </Text>
        )}
      </Flex>
    );
  };

  const showScore = (
    title: string,
    qualityScore: string,
    qualityPercentage: string
  ) => (
    <Flex
      css={{ marginTop: '15px', marginBottom: '15px' }}
      justify="space-between"
    >
      <Text
        css={{ fontSize: '18px', fontWeight: '$bold' }}
        data-testid="smart-choice-sub-heading"
      >
        {title}
      </Text>
      <Text css={{ fontSize: '15px', fontWeight: '$bold' }}>
        {t('SMART_CHOICE.AUDIT_SCREEN.Score_percentage', {
          qualityScore,
          qualityPercentage,
        })}
      </Text>
    </Flex>
  );

  const showMeasures = (
    val: string,
    key: string | null | undefined,
    formatter: (value: string | null | undefined) => string = formattedValue
  ) => (
    <Flex justify="space-between">
      <Text css={{ fontSize: '15px' }} data-testid="smart-choice-sub-heading">
        {val}
      </Text>
      <Text css={{ fontSize: '15px', fontWeight: '$bold' }}>
        {key !== '' ? formatter(key) : t('N/A')}
      </Text>
    </Flex>
  );

  const getTieredText = (providerTierDesignation: string) => {
    if (isTieredPlan !== true) {
      return t('N/A');
    }
    if (providerTierDesignation === 'Y') {
      return 'Y';
    } else if (providerTierDesignation === 'N') {
      return 'N';
    }

    return t('N/A');
  };

  const showValue = (
    fieldVal: string,
    testIdCategory = 'generic',
    testIdName = 'value'
  ) =>
    fieldVal !== '' ? (
      <Text
        css={{ fontSize: '15px', fontWeight: '$bold' }}
        data-testid={`smart-choice-details-${testIdCategory}-${testIdName}`}
      >
        {fieldVal}
      </Text>
    ) : (
      <Text
        css={{ fontSize: '15px', fontWeight: '$bold' }}
        data-testid={`smart-choice-details-${testIdCategory}-${testIdName}`}
      >
        {t('N/A')}
      </Text>
    );

  const verificationSectionTitle = isFacilityContext
    ? t('SMART_CHOICE.AUDIT_SCREEN.Information Accuracy')
    : t('SMART_CHOICE.AUDIT_SCREEN.Updated Information');

  return (
    <React.Fragment>
      <StyledDrawer
        data-auto-testid="onboarding-smart-choice-drawer"
        data-testid="onboarding-smart-choice-drawer"
        isOpen={isOpen}
        onClose={handleCloseDrawer}
        position={properties.position}
        size={properties.drawerSize}
        title={
          <Heading
            color="$primary"
            css={{ fontSize: '32.45px !important' }}
            data-testid="onboarding-smart-choice-heading"
            offset={5}
            textAlign="center"
          >
            {t('SMART_CHOICE.AUDIT_SCREEN.Smart Choice Details')}
          </Heading>
        }
      >
        <ContentWrapper style={{ padding: mobileScreen ? '0px' : '16px' }}>
          {renderOverallRecommendationScore(
            t,
            qualityScore,
            benefitsScore,
            convenienceScore,
            personalFitScore,
            verificationScore,
            randomizationScore
          )}
          {/* Quality Scores */}
          {showScore(
            t('SMART_CHOICE.AUDIT_SCREEN.Quality'),
            qualityScore,
            qualityPercentage
          )}
          {visibleFields.quality.fields.includes('credentialed') &&
            showMeasures(
              `${t(
                visibleFields.quality.labels?.credentialed ||
                  'SMART_CHOICE.AUDIT_SCREEN.Provider Credentialing'
              )}:`,
              quality.credentialed
            )}
          {visibleFields.quality.fields.includes('leapfrog') &&
            showMeasures(
              `${t(
                'SMART_CHOICE.AUDIT_SCREEN.Leapfrog Patient Safety Rating'
              )}:`,
              quality.leapfrogPatientSafetyRatings
            )}
          {visibleFields.quality.fields.includes('accreditation') &&
            showMeasures(
              `${t('SMART_CHOICE.AUDIT_SCREEN.Accreditation')}:`,
              quality.accreditation
            )}
          {visibleFields.quality.fields.includes('uhpd') &&
            showMeasures(
              `${t('SMART_CHOICE.AUDIT_SCREEN.UHPD')}:`,
              supressPremium === 'Y' ||
                quality.premiumDesignation?.toLowerCase() === 'na'
                ? ''
                : quality.premiumDesignation?.toLowerCase() === 'y'
                ? 'y'
                : 'n'
            )}
          {visibleFields.quality.fields.includes('llmScore') && (
            <Flex justify="space-between">
              <Text>{`${t('SMART_CHOICE.AUDIT_SCREEN.LLM Score')}:`}</Text>
              <Text css={{ fontSize: '15px', fontWeight: '$bold' }}>
                {llmScoreText}
              </Text>
            </Flex>
          )}

          {/* Benefits Score */}
          {showScore(
            t('SMART_CHOICE.AUDIT_SCREEN.Benefits'),
            benefitsScore,
            benefitsPercentage
          )}
          {visibleFields.benefits.fields.includes('inNetworkStatus') &&
            showMeasures(
              `${t('SMART_CHOICE.AUDIT_SCREEN.Network Status')}:`,
              benefits.inNetworkStatus
            )}

          {visibleFields.benefits.showPractitionerFields ? (
            // Practitioner-specific benefit fields (only when UHPD field is shown = practitioner)
            <>
              <Flex justify="space-between">
                <Text
                  css={{ fontSize: '15px' }}
                  data-testid="smart-choice-sub-heading"
                >
                  {`${t('SMART_CHOICE.AUDIT_SCREEN.Cost Efficiency')}:`}
                </Text>
                <Text css={{ fontSize: '15px', fontWeight: '$bold' }}>
                  {benefits.costEfficiencyRating !== ''
                    ? benefits.costEfficiencyRating
                    : t('N/A')}
                </Text>
              </Flex>
              <Flex justify="space-between">
                <Text
                  css={{ fontSize: '15px' }}
                  data-testid="smart-choice-sub-heading"
                >
                  {`${t('SMART_CHOICE.AUDIT_SCREEN.Tier')}:`}
                </Text>
                <Text css={{ fontSize: '15px', fontWeight: '$bold' }}>
                  {getTieredText(benefits.providerTierDesignation)}
                </Text>
              </Flex>
              {showMeasures(
                `${t('SMART_CHOICE.AUDIT_SCREEN.ACO affiliation')}:`,
                isAcoBenefitPlan ? affiliation : ''
              )}
              {showMeasures(
                `${t('SMART_CHOICE.AUDIT_SCREEN.Green Diamond')}:`,
                isCustomPreferredPolicy ? preferredProviderStatus : ''
              )}
              <Flex justify="space-between">
                <Text css={{ fontSize: '15px' }}>{`${t(
                  'SMART_CHOICE.AUDIT_SCREEN.Cost Indicator'
                )}:`}</Text>
                {showValue(benefits.costIndicator)}
              </Flex>
              <Flex justify="space-between">
                <Text css={{ fontSize: '15px' }}>{`${t(
                  'SMART_CHOICE.AUDIT_SCREEN.Billing Code'
                )}:`}</Text>
                {showValue(billingCode, 'other', 'billingCode')}
              </Flex>
              <Flex justify="space-between">
                <Text css={{ fontSize: '15px' }}>{`${t(
                  'SMART_CHOICE.AUDIT_SCREEN.Cost Band'
                )}:`}</Text>
                {showValue(bandedCost, 'other', 'bandedCost')}
              </Flex>
            </>
          ) : (
            // Facility-specific benefit fields (7 fields - ALL baseline Y, correct order per table)
            <>
              <Flex justify="space-between">
                <Text css={{ fontSize: '15px' }}>
                  {`${t('SMART_CHOICE.AUDIT_SCREEN.Provider Tiering Level')}:`}
                </Text>
                <Text css={{ fontSize: '15px', fontWeight: '$bold' }}>
                  {benefits.providerTieringLevel !== ''
                    ? benefits.providerTieringLevel
                    : t('N/A')}
                </Text>
              </Flex>
              <Flex justify="space-between">
                <Text css={{ fontSize: '15px' }}>
                  {`${t('SMART_CHOICE.AUDIT_SCREEN.Cost Efficiency')}:`}
                </Text>
                <Text css={{ fontSize: '15px', fontWeight: '$bold' }}>
                  {benefits.costEfficiencyRating !== ''
                    ? benefits.costEfficiencyRating
                    : t('N/A')}
                </Text>
              </Flex>
              {showMeasures(
                `${t(
                  'SMART_CHOICE.AUDIT_SCREEN.Preferred Lab Network Indicator'
                )}:`,
                benefits.preferredLabNetworkIndicator
              )}
              {showMeasures(
                `${t(
                  'SMART_CHOICE.AUDIT_SCREEN.Oxford Preferred Lab Network Indicator'
                )}:`,
                benefits.oxfordPreferredLabNetworkIndicator
              )}
              {showMeasures(
                `${t('SMART_CHOICE.AUDIT_SCREEN.Designated Diagnostic Lab')}:`,
                benefits.designatedDiagnosticLab
              )}
              {showMeasures(
                `${t(
                  'SMART_CHOICE.AUDIT_SCREEN.Free Standing Facility Indicator'
                )}:`,
                benefits.freeStandingFacilityIndicator
              )}
            </>
          )}

          {/* Convenience Score */}
          {showScore('Convenience', convenienceScore, conveniencePercentage)}
          {visibleFields.convenience.fields.includes(
            'appointmentAvailability'
          ) && (
            <Flex justify="space-between">
              <Text css={{ fontSize: '15px' }}>{`${t(
                'SMART_CHOICE.AUDIT_SCREEN.Appointment Availability Window'
              )}:`}</Text>
              {showValue(convenience.appointmentAvailability)}
            </Flex>
          )}
          {visibleFields.convenience.fields.includes('eveningAppointments') &&
            showMeasures(
              `${t('SMART_CHOICE.AUDIT_SCREEN.Evening Availability')}:`,
              convenience.eveningAppointments
            )}
          {visibleFields.convenience.fields.includes('weekendAppointments') &&
            showMeasures(
              `${t('SMART_CHOICE.AUDIT_SCREEN.Weekend Availability')}:`,
              convenience.weekendAppointments
            )}
          {visibleFields.convenience.fields.includes('acceptingNewPatients') &&
            showMeasures(
              `${t('SMART_CHOICE.AUDIT_SCREEN.Accepting New Patients')}:`,
              convenience.acceptingNewPatients
            )}
          {visibleFields.convenience.fields.includes(
            'onlineAppointmentScheduling'
          ) &&
            showMeasures(
              `${t(
                'SMART_CHOICE.AUDIT_SCREEN.Online Appointment scheduling'
              )}:`,
              convenience.onlineAppointmentScheduling
            )}
          {visibleFields.convenience.fields.includes('distance') && (
            <Flex justify="space-between">
              <Text css={{ fontSize: '15px' }}>{`${t(
                'SMART_CHOICE.AUDIT_SCREEN.Distance'
              )}:`}</Text>
              {distance !== '' ? (
                <Text css={{ fontSize: '15px', fontWeight: '$bold' }}>
                  {formattedDist}
                </Text>
              ) : (
                <Text css={{ fontSize: '15px', fontWeight: '$bold' }}>
                  {t('N/A')}
                </Text>
              )}
            </Flex>
          )}
          {visibleFields.convenience.fields.includes('virtualAppointments') &&
            showMeasures(
              `${t('SMART_CHOICE.AUDIT_SCREEN.Virtual Appointments')}:`,
              convenience.virtualAppointments
            )}
          {visibleFields.convenience.fields.includes('memberPreferredRadius') &&
            showMeasures(
              `${t(
                'SMART_CHOICE.AUDIT_SCREEN.Member Preferred Radius Indicator'
              )}:`,
              convenience.memberPreferredRadius
            )}
          {/* Personal Fit Score */}
          {showScore(
            t('SMART_CHOICE.AUDIT_SCREEN.Personal Fit'),
            personalFitScore,
            personalFitPercentage
          )}
          {visibleFields.personalFit.fields.includes('language') &&
            showMeasures(
              `${t(
                visibleFields.personalFit.labels?.language ||
                  'SMART_CHOICE.AUDIT_SCREEN.Language Spoken - Practitioner'
              )}:`,
              personalFit.languageSpoken,
              isFacilityContext
                ? (value) =>
                    value ? getLanguageFormatted(value).toUpperCase() : t('N/A')
                : formattedValue
            )}
          {visibleFields.personalFit.fields.includes('preferredLanguages') && (
            <Flex justify="space-between">
              <Text>{`${t(
                'SMART_CHOICE.AUDIT_SCREEN.Preferred Languages'
              )}:`}</Text>
              {preferencesData?.languagesSpoken &&
              preferencesData?.languagesSpoken !== '' ? (
                <Text css={{ fontSize: '15px', fontWeight: '$bold' }}>
                  {getLanguageFormatted(preferencesData?.languagesSpoken)}
                </Text>
              ) : (
                <Text css={{ fontSize: '15px', fontWeight: '$bold' }}>
                  {t('N/A')}
                </Text>
              )}
            </Flex>
          )}
          {visibleFields.personalFit.fields.includes('gender') && (
            <Flex justify="space-between">
              <Text css={{ fontSize: '15px' }}>{`${t(
                'SMART_CHOICE.AUDIT_SCREEN.Gender'
              )}:`}</Text>
              {showValue(personalFit.gender)}
            </Flex>
          )}
          {visibleFields.personalFit.fields.includes('boardCertification') &&
            showMeasures(
              `${t('SMART_CHOICE.AUDIT_SCREEN.Board Certified')}:`,
              personalFit.boardCertification
            )}
          {visibleFields.personalFit.fields.includes('consumerRating') && (
            <Flex justify="space-between">
              <Text css={{ fontSize: '15px' }}>{`${t(
                'SMART_CHOICE.AUDIT_SCREEN.Consumer Rating'
              )}:`}</Text>
              {showValue(personalFit.consumerRating)}
            </Flex>
          )}
          {visibleFields.personalFit.fields.includes(
            'consumerRatingSurveyVolume'
          ) && (
            <Flex justify="space-between">
              <Text css={{ fontSize: '15px' }}>{`${t(
                'SMART_CHOICE.AUDIT_SCREEN.Consumer Rating Survey Volume'
              )}:`}</Text>
              {showValue(personalFit.consumerRatingSurveyVolume)}
            </Flex>
          )}
          {visibleFields.personalFit.fields.includes(
            'midPointRatingIndicator'
          ) &&
            showMeasures(
              `${t('SMART_CHOICE.AUDIT_SCREEN.Mid Point Rating Indicator')}:`,
              personalFit.consumerRatingMidPointStatus
            )}

          {/* Updated information score section */}
          {visibleFields.verification.showSection && (
            <>
              {showScore(
                verificationSectionTitle,
                verificationScore,
                verificationPercentage
              )}
              <Flex
                data-testid="audit-model-address-confidence-indicator"
                justify="space-between"
              >
                <Text css={{ fontSize: '15px' }}>{`${t(
                  'SMART_CHOICE.AUDIT_SCREEN.Address Confidence Indicator'
                )}:`}</Text>
                {showValue(officeLocationScore)}
              </Flex>
              <Flex
                data-testid="audit-model-phone-confidence-recommendation-score"
                justify="space-between"
              >
                <Text css={{ fontSize: '15px' }}>{`${t(
                  'SMART_CHOICE.AUDIT_SCREEN.Phone Confidence Recommendation Score'
                )}:`}</Text>
                {showValue(phoneConfidenceRecommendationScore)}
              </Flex>
            </>
          )}

          {/* Randomization Score - Show for both practitioners and facilities */}
          {visibleFields.randomization.showSection && (
            <>
              {showScore(
                t('SMART_CHOICE.AUDIT_SCREEN.Randomization'),
                randomizationScore,
                randomizationPercentage
              )}
              <Flex justify="space-between">
                <Text css={{ fontSize: '15px' }}>{`${t(
                  'SMART_CHOICE.AUDIT_SCREEN.Randomization Key'
                )}:`}</Text>
                {showValue(randomizationKey)}
              </Flex>
            </>
          )}
          <Flex css={{ marginTop: '30px' }}>
            <Text css={{ fontSize: '15px' }}>
              {t('SMART_CHOICE.AUDIT_SCREEN.Not_A_Match')}
            </Text>
          </Flex>
        </ContentWrapper>
      </StyledDrawer>
    </React.Fragment>
  );
};
